import React from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  type OpenChangeReason,
} from '@floating-ui/react';

// バナー詳細のダイアログ（z-index 1050）の上にも出す
const Z_INDEX = 1100;

interface TagPopoverProps {
  open: boolean;
  /** reason で、Esc で閉じた（'escape-key'）のか外を押した（'outside-press'）のかが分かる */
  onOpenChange: (open: boolean, event?: Event, reason?: OpenChangeReason) => void;
  label: string;
  /** 開閉を受け持つボタン。ref と props をそのまま付ける */
  renderTrigger: (trigger: {
    ref: (node: HTMLElement | null) => void;
    props: Record<string, unknown>;
  }) => React.ReactNode;
  children: React.ReactNode;
}

const TagPopover: React.FC<TagPopoverProps> = ({ open, onOpenChange, label, renderTrigger, children }) => {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    // 中の入力欄は開いた直後、位置が決まる前にフォーカスされる。absolute だとその間はページの左上にあり、
    // スクロールした一覧から開くとページの一番上まで飛ぶ。fixed なら画面の左上なので飛ばない
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ]);

  return (
    <>
      {renderTrigger({
        ref: refs.setReference,
        // カードの onClick（詳細を開く）まで届かないよう止める
        props: getReferenceProps({ onClick: (event: React.MouseEvent) => event.stopPropagation() }),
      })}
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
            <div
              ref={refs.setFloating}
              aria-label={label}
              style={{ ...floatingStyles, zIndex: Z_INDEX }}
              className="w-64 rounded-lg border border-warm-border bg-white p-2 text-sm text-dark shadow-xl"
              {...getFloatingProps({ onClick: (event: React.MouseEvent) => event.stopPropagation() })}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default TagPopover;
